/**
 * 滚动动画 Hook - v2.0 简化版
 *
 * 核心理念：
 * 1. 监听容器元素进入视口
 * 2. 使用 React state 控制 isVisible
 * 3. className 由 state 控制，重渲染不会丢失
 * 4. 符合 React 理念，不直接操作 DOM
 */

import { useEffect, useRef, useState } from 'react';

export interface ScrollAnimationOptions {
    /**
     * IntersectionObserver threshold
     * @default 0.1
     */
    threshold?: number | number[];

    /**
     * IntersectionObserver rootMargin
     * @default '-5% 0px'
     */
    rootMargin?: string;

    /**
     * 是否只触发一次（触发后停止观察）
     * @default true
     */
    once?: boolean;

    /**
     * 离开视口时是否隐藏（用于重复动画）
     * @default false
     */
    removeOnLeave?: boolean;

    /**
     * 默认是否可见（用于编辑模式）
     * @default false
     */
    defaultVisible?: boolean;

    /**
     * 是否启用调试模式
     * @default false
     */
    debug?: boolean;
}

/**
 * 滚动动画 Hook
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { ref, isVisible } = useScrollAnimation();
 *
 *   return (
 *     <div ref={ref}>
 *       <h1 className={isVisible ? 'animate-visible' : ''}>Title</h1>
 *       {items.map((item, i) => (
 *         <div
 *           key={i}
 *           className={isVisible ? 'animate-visible' : ''}
 *           style={{ '--animate-delay': `${i * 0.15}s` }}
 *         >
 *           {item.content}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useScrollAnimation(options: ScrollAnimationOptions = {}) {
    const { threshold = 0.1, rootMargin = '-10% 0px', once = true, removeOnLeave = false, defaultVisible = false, debug = false } = options;

    const [isVisible, setIsVisible] = useState(defaultVisible);
    const ref = useRef<any>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        // 创建 IntersectionObserver
        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // 进入视口
                    setIsVisible(true);

                    if (debug) {
                        console.log('[useScrollAnimation] 元素进入视口:', element);
                    }

                    // 如果只触发一次，停止观察
                    if (once && observerRef.current) {
                        observerRef.current.disconnect();
                        if (debug) {
                            console.log('[useScrollAnimation] 停止观察:', element);
                        }
                    }
                } else {
                    // 离开视口
                    if (removeOnLeave) {
                        setIsVisible(false);
                        if (debug) {
                            console.log('[useScrollAnimation] 元素离开视口，隐藏:', element);
                        }
                    }
                }
            },
            { threshold, rootMargin },
        );

        observerRef.current.observe(element);

        if (debug) {
            console.log('[useScrollAnimation] 开始观察:', element);
        }

        // 清理函数
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                if (debug) {
                    console.log('[useScrollAnimation] 清理观察器:', element);
                }
            }
        };
    }, [threshold, rootMargin, once, removeOnLeave, debug]);

    return { ref, isVisible };
}

/**
 * className 辅助函数
 *
 * @example
 * ```tsx
 * <div className={cn(isVisible, 'my-class')}>Content</div>
 * // 输出: "my-class animate-visible" 或 "my-class"
 * ```
 */
export function cn(isVisible: boolean, baseClass = ''): string {
    return `${baseClass} ${isVisible ? 'animate-visible' : ''}`.trim();
}

/**
 * 获取错开动画的 style
 *
 * @example
 * ```tsx
 * {items.map((item, i) => (
 *   <div
 *     key={i}
 *     className={cn(isVisible)}
 *     style={getStaggerStyle(i, 0.15)}
 *   >
 *     {item}
 *   </div>
 * ))}
 * ```
 */
export function getStaggerStyle(index: number, delay = 0.15): React.CSSProperties {
    return {
        '--animate-delay': `${index * delay}s`,
    } as React.CSSProperties;
}
